<template>
  <div :data-test="`compliance-row-${compliance.name}`">
    <WdsCollapse v-if="showsStatusPill" v-model:open="open" :content-id="compliance.name">
      <template #title>
        <WdsStack justify-space-between>
          <WdsText :text="compliance.title" />
          <WdsStack align-center justify-center gap-sm class="tw-flex tw-items-center">
            <ComplianceStatusLabel :completed="compliance.completed" sm />
            <WdsStack
              v-if="showData"
              justify-center
              align-center
              gap-xs
              class="tw-transition-all"
              :class="{
                'tw-rotate-0': open,
                'tw-rotate-180': !open,
              }"
            >
              <WdsIcon name="chevron-down" :size="16" primary />
            </WdsStack>
            <div v-else class="tw-w-4 tw-h-4" />
          </WdsStack>
        </WdsStack>
      </template>
      <template #content>
        <div v-if="!showData"></div>
        <div v-else-if="compliance.data.length === 0" class="tw-pb-3">
          <WdsText :text="compliance.description" secondary sm />
        </div>
        <div v-else>
          <ComplianceDataEntry v-for="(entry, idx) in compliance.data" :key="idx" :entry="entry" :section-title="compliance.title" />
        </div>
      </template>
    </WdsCollapse>
    <div v-else>
      <WdsStack justify-space-between>
        <WdsText :text="compliance.title" />
      </WdsStack>
      <div v-if="compliance.data.length === 0" class="tw-pb-3">
        <WdsText :text="complianceReviewDescription" secondary sm />
      </div>
      <div v-else>
        <ComplianceDataEntry v-for="(entry, idx) in compliance.data" :key="idx" :entry="entry" :section-title="compliance.title" />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue'
import { WdsIcon, WdsStack, WdsText, WdsCollapse } from '@worksome/wds-components'
import { FragmentType, graphql, useFragment } from '@/gql'
import { ComplianceName } from '@/gql/graphql'
import { ComplianceDataEntry } from '@/components/ComplianceData'
import ComplianceStatusLabel from '@/components/compliances/ComplianceStatusLabel.vue'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import { user } from '@/composables/useUser'

const ComplianceSection = graphql(`
  fragment ComplianceSection on Compliance {
    name
    title
    completed
    description
    data {
      ...ComplianceDataField
      ...ComplianceDataGroup
      ...ComplianceDataFile
      ...ComplianceDataBusinessEntity
      ...ComplianceDataAddress
      ...ComplianceDataUser
      ...ComplianceDataBankDetail
      ...ComplianceDataClassification
      ...ComplianceDataRestrictedDocument
      ...ComplianceDataStatus
      ...ComplianceDataDate
    }
  }
`)

const props = defineProps<{ row: FragmentType<typeof ComplianceSection> }>()
const compliance = computed(() => useFragment(ComplianceSection, props.row))

const showData = computed(
  () => user.value.hasPermission('manage-compliance-tracker') && compliance.value.data.length > 0,
)

const complianceReviewDescription = computed(() =>
  compliance.value.completed ? $t('Completed review') : compliance.value.description,
)

const showsStatusPill = computed(
  () =>
    compliance.value.name !== ComplianceName.VerifiedWorkerCompliance &&
    compliance.value.name !== ComplianceName.VerifiedRecruiterCompliance,
)

const open = ref(!showsStatusPill.value || compliance.value.completed)
</script>
